import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import { Grid } from "@mui/material";
import MDTypography from "components/MDTypography";
import { t } from "i18next";
import React, { useEffect, useState } from "react";

import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import useAlert from "hooks/useAlert";
import AlertMessage from "components/UI/AlertMessage";
import DynamicPieChart from "../charts/piechart";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import MBConfirmationPopup from "components/MBConfirmationPopup";

import Chip from "@mui/material/Chip";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  styled,
  ThemeProvider,
  createTheme,
  Link,
} from "@mui/material";

import Unauthorized from "components/UI/Unauthorized";
import { usePermissions } from "context/PermissionsContext";
import useAPI from "hooks/useAPI";
import WorkerStatus from "./worker_status";

// Custom theme configuration
const theme = createTheme({});

const initialServicesData = [
  {
    id: 1,
    name: "Global Worker Hub (GWH)",
    key: "GWH",
  },
  {
    id: 2,
    name: "Global Worker Manager (GWM)",
    key: "GWM",
  },
  {
    id: 3,
    name: "Background Worker Handler (BWH)",
    key: "BWH",
  },
  {
    id: 4,
    name: "General Background Worker Monitor (GBWM)",
    key: "GBWM",
  },
  {
    id: 5,
    name: "Kafka Manager (KAFKA)",
    key: "KAFKA",
    detail_page: "/kafka",
  },
  // {
  //   id: 999,
  //   name: "Others",
  //   key: "others",
  // },
];
const StatusButton = styled(Button)(({ status }) => ({
  borderRadius: "7px",
  textTransform: "captitalize",
  fontWeight: 500,
  backgroundColor: status === "running" ? "rgba(240,82,82,1)" : "rgba(14,159,110,1)",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
  // color: "white",
  padding: "8px 16px",
  letterSpacing: "0.5px",
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: status === "running" ? "rgba(240,82,82,0.8)" : "rgba(14,159,110,0.8)",
  },
  padding: "5px 10px",
}));

const StyledButton = styled(Button)(({ status }) => ({
  borderRadius: "7px",
  textTransform: "captitalize",
  fontWeight: 500,
  backgroundColor: "rgb(228, 228, 228)",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
  // color: "white",
  padding: "8px 16px",
  letterSpacing: "0.5px",
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: "rgb(211, 211, 211)",
  },
  padding: "5px 10px",
  color: "black",
}));

const tableStyles = {
  borderRadius: "8px",
  boxShadow: 3,
  overflow: "hidden",
};

const headerStyles = {
  backgroundColor: "#f5f5f5",
  height: "50%",
  "& .MuiTableCell-root": {
    padding: "8px 16px",
    fontSize: "0.875rem",
  },
};

const Dashboard = () => {
  const [gunicornData, setGunicornData] = useState(null);
  const { errorState, showAlert } = useAlert();
  const [services, setServices] = useState(initialServicesData);
  const { hasPermission } = usePermissions();
  const { swm_api } = useAPI();
  const [loading, setLoading] = useState(true);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [serviceName, setServicesName] = useState("");
  const [startingService, setStartingService] = useState(null);
  const [otherProcessesData, setOtherProcessesData] = useState(null);

  const handleDialogOpen = (service) => {
    setDialogOpen(true);
    setServicesName(service);
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    setDialogOpen(false);
  };

  const requiredPermission = "can_view_smm";
  if (!hasPermission(requiredPermission)) {
    return <Unauthorized />;
  }

  const ColorBar = styled("div")(({ status }) => ({
    width: "0.5rem",
    height: "2.5rem",
    position: "absolute",
    left: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: status === "running" ? "rgba(49,196,141,1)" : "rgba(249,128,128,1)",
    borderRadius: "25px",
  }));

  const handleToggleService = async (name) => {
    setLoadingBtn(true);
    setStartingService(name);
    const service = services.find((s) => s.key === name);

    if (!service) return;

    const { endpoint } =
      service.status === "running"
        ? {
            endpoint: `service_monitor/${name}/stop`,
          }
        : {
            endpoint: `service_monitor/${name}/start`,
          };

    try {
      const { data } = await swm_api.post(endpoint);

      if (data.status === "success") {
        await fetchData();
        showAlert(
          "success",
          t("lang.serviceSuccessMessage", {
            name: name,
            status: service.status === "running" ? t("lang.stopped") : t("lang.started"),
          })
        );
      } else if (data.status === "failed") {
        showAlert(
          "error",
          t("lang.serviceErrorMessage", {
            name: name,
            status: service.status === "running" ? t("lang.stopped") : t("lang.started"),
          })
        );
        return;
      }
    } catch (err) {
      showAlert(
        "error",
        t("lang.serviceErrorMessage", {
          name: name,
          status: service.status === "running" ? t("lang.stopped") : t("lang.started"),
        })
      );
    } finally {
      setLoading(false);
      setLoadingBtn(false);
      handleDialogClose();
      setStartingService(null);
    }
  };

  const fetchData = async () => {
    try {
      const { data } = await swm_api.get(`service_monitor/`);
      setGunicornData(data.data);
      if (data?.others) {
        setOtherProcessesData(data?.others);
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, 180000); // Fetch data every 3 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  useEffect(() => {
    if (gunicornData) {
      setServices((prevServices) =>
        prevServices.map((service) => {
          const serviceData = gunicornData[service.key];
          return serviceData
            ? {
                ...service,
                cpu_usage: `${serviceData.cpu_percent.toFixed(2)}%`,
                memory_usage: `${serviceData.memory_percent.toFixed(2)}%`,
                status: serviceData.status,
              }
            : {
                ...service,
                cpu_usage: "0%",
                memory_usage: "0%",
                status: "stopped",
              };
        })
      );
    }
  }, [gunicornData]);

  const cpuData = gunicornData?.systemstatus?.cpu
    ? [
        { value: gunicornData.systemstatus.cpu.used.toFixed(2), label: t("lang.used") },
        { value: gunicornData.systemstatus.cpu.free.toFixed(2), label: t("lang.free") },
      ]
    : [];

  const memoryData = gunicornData?.systemstatus?.memory
    ? [
        { value: gunicornData.systemstatus.memory.used.toFixed(2), label: t("lang.used") },
        { value: gunicornData.systemstatus.memory.free.toFixed(2), label: t("lang.free") },
      ]
    : [];

  const pagesList = [
    {
      name: t("lang.dashboard"),
      route: "",
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar pagesList={pagesList} />
      <WorkerStatus isIframe={false} />
    </DashboardLayout>
  );
};

export default Dashboard;
